/*
 * Tipos do wire do modulo de Cadastros (FE-4 CP-A — ADR 0026).
 *
 * Espelham FIELMENTE os schemas Pydantic do backend em `master`
 * (`flow/api/cadastros/*`). Regra de projeto INVIOLAVEL: TODO valor monetario e
 * `string` aqui — nunca `number`. O wire entrega `NUMERIC(18,2)` serializado como
 * string decimal (ex.: `"0.00"`); converte-lo para `number` em JS perderia
 * precisao (IEEE-754 nao representa todo decimal exato e estoura acima de
 * `Number.MAX_SAFE_INTEGER`). O unico campo monetario daqui — `saldo_disponivel`
 * de `ContaCorrenteOut` — permanece STRING e e read-only (nao entra em In/Patch).
 *
 * Convencao snake_case: os campos abaixo copiam o nome EXATO do JSON do backend
 * (snake_case), sem traducao na borda. A senha do usuario e WRITE-ONLY: vai no
 * corpo de criacao/edicao (`UsuarioIn`/`UsuarioPatch`) mas NUNCA volta no `Out`
 * (o backend jamais serializa hash/senha — ver `UsuarioOut`).
 */

/* ───────────────────────── Empresas ───────────────────────── */

/**
 * Empresa cadastrada (`GET /api/empresas`, `.../{id}`).
 *
 * Filtrada pelo escopo do usuario (acao `cadastrar_infraestrutura`). `fantasia`
 * e anulavel (nem toda empresa cadastra nome fantasia). `criado_em`/`atualizado_em`
 * sao ISO datetime. `cnpj` vem em claro (campo de cadastro, nao PII de extrato).
 */
export interface EmpresaOut {
  id: string;
  cnpj: string;
  razao_social: string;
  fantasia: string | null;
  ativa: boolean;
  criado_em: string;
  atualizado_em: string;
}

/** Corpo do `POST /api/empresas`. `fantasia` opcional (omitida ou null). */
export interface EmpresaIn {
  cnpj: string;
  razao_social: string;
  fantasia?: string | null;
}

/**
 * Corpo do `PATCH /api/empresas/{id}` (campos parciais).
 *
 * O `cnpj` NAO e editavel (identidade fiscal da empresa) — por isso ausente aqui.
 */
export interface EmpresaPatch {
  razao_social?: string;
  fantasia?: string | null;
  ativa?: boolean;
}

/* ───────────────────────── Bancos ───────────────────────── */

/** Banco do catalogo global (`GET /api/bancos`, `.../{id}`). */
export interface BancoOut {
  id: string;
  codigo_compe: string;
  nome: string;
}

/** Corpo do `POST /api/bancos`. */
export interface BancoIn {
  codigo_compe: string;
  nome: string;
}

/**
 * Corpo do `PATCH /api/bancos/{id}`.
 *
 * So o `nome` e editavel — o `codigo_compe` e a chave do catalogo COMPE.
 */
export interface BancoPatch {
  nome?: string;
}

/* ───────────────────────── Contas correntes ───────────────────────── */

/**
 * Tipo da conta corrente — Literal EXATO do backend.
 *
 * `TIPOS_CONTA` e a const array dos valores para a UI montar o `<select>`
 * (CP-B/CP-C). `as const` torna a tupla read-only e o tipo `TipoConta` derivado
 * dela permanece sincronizado: acrescentar um valor aqui propaga ao tipo.
 */
export const TIPOS_CONTA = ["corrente", "poupanca", "garantida"] as const;
export type TipoConta = (typeof TIPOS_CONTA)[number];

/**
 * Conta corrente de uma empresa
 * (`GET /api/empresas/{empresaId}/contas-correntes`, `.../{contaId}`).
 *
 * Nested por empresa (isolamento IDOR via path no backend). `saldo_disponivel` e
 * STRING decimal money (ver nota de precisao no topo) e e READ-ONLY — nunca entra
 * em `ContaCorrenteIn`/`Patch`. `empresa_id`/`banco_id` sao FKs; `criado_em`/
 * `atualizado_em` ISO datetime.
 */
export interface ContaCorrenteOut {
  id: string;
  empresa_id: string;
  banco_id: string;
  agencia: string;
  numero: string;
  tipo: TipoConta;
  saldo_disponivel: string;
  ativa: boolean;
  criado_em: string;
  atualizado_em: string;
}

/**
 * Corpo do `POST /api/empresas/{empresaId}/contas-correntes`.
 *
 * SEM `empresa_id`: a empresa vem do PATH (o backend rejeita IDOR por divergencia
 * de path). SEM `saldo_disponivel`: o saldo e gerido por outro fluxo (semeio),
 * nunca pelo cadastro.
 */
export interface ContaCorrenteIn {
  banco_id: string;
  agencia: string;
  numero: string;
  tipo: TipoConta;
}

/** Corpo do `PATCH /api/empresas/{empresaId}/contas-correntes/{contaId}`. */
export interface ContaCorrentePatch {
  agencia?: string;
  numero?: string;
  tipo?: TipoConta;
  ativa?: boolean;
}

/* ───────────────────────── Usuarios ───────────────────────── */

/**
 * Usuario cadastrado (`GET /api/usuarios`, `.../{id}`).
 *
 * SEM senha/hash — o backend JAMAIS serializa credencial no `Out`. `criado_em`/
 * `atualizado_em` ISO datetime.
 */
export interface UsuarioOut {
  id: string;
  email: string;
  nome: string;
  ativo: boolean;
  criado_em: string;
  atualizado_em: string;
}

/**
 * Corpo do `POST /api/usuarios`.
 *
 * `senha` e WRITE-ONLY e OBRIGATORIA na criacao — vai no corpo mas nunca volta no
 * `UsuarioOut`. `ativo` opcional (default do backend).
 */
export interface UsuarioIn {
  email: string;
  nome: string;
  senha: string;
  ativo?: boolean;
}

/**
 * Corpo do `PATCH /api/usuarios/{id}` (campos parciais).
 *
 * `senha` WRITE-ONLY e OPCIONAL: presente troca a credencial, ausente mantem.
 * Nunca volta no `Out`. O `email` NAO e editavel (identidade de login).
 */
export interface UsuarioPatch {
  nome?: string;
  senha?: string;
  ativo?: boolean;
}

/* ───────────────────────── Perfis ───────────────────────── */

/** Perfil de acesso, somente leitura (`GET /api/perfis`). */
export interface PerfilOut {
  id: string;
  nome: string;
}
